import React, { useMemo } from "react";

import { Card } from "@/components/flowbite/card";
import IconButton from "@/components/flowbite/icon-button/icon-button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/flowbite/tooltip";
import Typography from "@/components/flowbite/typography/typography";
import { CancelIcon } from "@/components/icons-lib";
import { LinkifiedText } from "@/components/linkified-text/linkified-text";
import { PolygonPreview } from "@/components/maps/polygon-preview";
import { dictionaries } from "@/dictionaries/content";
import { getBoundaryPolygonPreviewColors } from "@/providers/maps/utils/get-boundary-polygon-preview-colors";
import { getFirstRingFromGeometry } from "@/providers/maps/utils/get-first-ring-from-geometry";
import { resolveBoundaryShapeDisplayValueFromSchools } from "@/providers/maps/utils/resolve-unassigned-boundary-display.utils";
import { useGetCustomLayerBoundaryDetailsQuery } from "@/providers/store/api/custom-layers/custom-layers";
import { useGetMapLevelBoundaryQuery } from "@/providers/store/api/map-levels/map-levels";
import { useGetSchoolWithDetailsQuery } from "@/providers/store/api/schools/schools";
import { useAppSelector } from "@/providers/store/hooks";
import { primaryBoundarySchoolIdentity } from "@/shared/utils/boundary-school-links.util";
import { resolveBoundarySchoolsDisplayName } from "@/shared/utils/resolve-boundary-schools-display-name";

import { SkeletonBox } from "./skeleton-box";

interface ShapeCardProps {
  label: string;
  value: string;
  onRemove?: () => void;
  previewCoordinates?: number[][];
  previewColor?: string;
  schoolId?: string;
  schoolDriveId?: string;
  customLayerId?: string;
  boundaryId?: string;
  mapLevelId?: string;
  /**
   * Per-shape NOTE. Non-empty → show; `null` → known empty (no fetch);
   * `undefined` → may resolve via getMapLevelBoundary when mapLevelId+boundaryId set.
   */
  note?: string | null;
}

const PREVIEW_SIZE = 40;

function resolveNoteText(
  noteProp: string | null | undefined,
  fetchedDescription: string | null | undefined,
): string {
  if (noteProp !== undefined) {
    return noteProp?.trim() ?? "";
  }

  return fetchedDescription?.trim() ?? "";
}

const ShapeCardComponent: React.FC<ShapeCardProps> = ({
  label,
  value,
  onRemove,
  previewCoordinates: previewCoordinatesProp,
  previewColor,
  schoolId,
  schoolDriveId: _schoolDriveId,
  customLayerId,
  boundaryId,
  mapLevelId,
  note: noteProp,
}) => {
  const districtId = useAppSelector((state) => state.district.selectedDistrict?.id);

  const shouldFetchCustomBoundary = Boolean(districtId && customLayerId && boundaryId);
  const shouldFetchSchoolDetails = Boolean(schoolId && !customLayerId && !mapLevelId);
  const shouldFetchMapLevelBoundary = Boolean(
    districtId && mapLevelId && boundaryId && noteProp === undefined && !customLayerId,
  );

  const {
    data: customDetails,
    isLoading: isCustomDetailsLoading,
  } = useGetCustomLayerBoundaryDetailsQuery(
    {
      districtId: districtId ?? "",
      layerId: customLayerId ?? "",
      boundaryId: boundaryId ?? "",
    },
    { skip: !shouldFetchCustomBoundary },
  );

  const {
    data: schoolDetails,
    isLoading: isSchoolDetailsLoading,
  } = useGetSchoolWithDetailsQuery(schoolId ?? "", {
    skip: !shouldFetchSchoolDetails || !schoolId,
  });

  const {
    data: mapLevelBoundary,
    isLoading: isMapLevelBoundaryLoading,
  } = useGetMapLevelBoundaryQuery(
    {
      districtId: districtId ?? "",
      levelId: mapLevelId ?? "",
      boundaryId: boundaryId ?? "",
    },
    { skip: !shouldFetchMapLevelBoundary },
  );

  const isCustomLoading = shouldFetchCustomBoundary && isCustomDetailsLoading;
  const isSchoolLoading = shouldFetchSchoolDetails && isSchoolDetailsLoading;
  const isMapLevelLoading = shouldFetchMapLevelBoundary && isMapLevelBoundaryLoading;
  const isLoading = isCustomLoading || isSchoolLoading || isMapLevelLoading;

  const noteText = resolveNoteText(noteProp, mapLevelBoundary?.description);

  const previewFromCustom = useMemo(() => {
    if (!customDetails?.geometryGeoJson) return undefined;
    try {
      const geometry = JSON.parse(customDetails.geometryGeoJson) as GeoJSON.Geometry;
      if (geometry.type !== "Polygon" && geometry.type !== "MultiPolygon") return undefined;
      return getFirstRingFromGeometry(geometry) ?? undefined;
    } catch {
      return undefined;
    }
  }, [customDetails?.geometryGeoJson]);

  const schoolBoundaryPreviews = useMemo(() => {
    const boundaries = schoolDetails?.schoolBoundaries ?? [];
    const scopedBoundaries = boundaryId
      ? boundaries.filter((boundary) => boundary.id === boundaryId)
      : boundaries;

    return scopedBoundaries
      .map((boundary) => {
        if (boundary.geometry == null) return null;
        const ring = getFirstRingFromGeometry(boundary.geometry);
        return ring ? { id: boundary.id, ring } : null;
      })
      .filter((preview): preview is { id: string; ring: number[][] } => preview != null);
  }, [schoolDetails?.schoolBoundaries, boundaryId]);

  const displayValue = useMemo(() => {
    if (customDetails) {
      return resolveBoundaryShapeDisplayValueFromSchools(customDetails.schools, value);
    }

    if (schoolDetails) {
      const boundaries = schoolDetails.schoolBoundaries ?? [];
      const scopedBoundary = boundaryId
        ? boundaries.find((boundary) => boundary.id === boundaryId)
        : boundaries[0];
      if (scopedBoundary?.schools?.length) {
        return resolveBoundarySchoolsDisplayName(scopedBoundary.schools, {
          unassignedLabel: schoolDetails.school?.name?.trim() ?? value,
        });
      }

      if (schoolDetails.school?.name?.trim()) {
        return schoolDetails.school.name;
      }
    }

    const mapLevelName = mapLevelBoundary?.name?.trim();
    if (mapLevelName) {
      return mapLevelName;
    }

    return value;
  }, [boundaryId, customDetails, mapLevelBoundary?.name, schoolDetails, value]);

  const polygonTint = useMemo(() => {
    const identity = customDetails
      ? primaryBoundarySchoolIdentity(customDetails.schools)
      : null;

    return getBoundaryPolygonPreviewColors({
      schoolId: identity?.schoolId ?? schoolId,
      schoolInternalId: identity?.schoolInternalId,
      schoolName: identity?.schoolName,
      schoolTypes: customDetails?.schoolTypes,
      color: customDetails?.color ?? schoolDetails?.color ?? mapLevelBoundary?.color ?? previewColor,
    });
  }, [
    customDetails,
    mapLevelBoundary?.color,
    previewColor,
    schoolDetails?.color,
    schoolId,
  ]);

  const previewContent = useMemo(() => {
    if (isLoading) {
      return <SkeletonBox className="size-10 shrink-0 rounded" />;
    }

    if (previewFromCustom) {
      return (
        <PolygonPreview
          coordinates={previewFromCustom}
          size={PREVIEW_SIZE}
          {...polygonTint}
        />
      );
    }

    if (schoolBoundaryPreviews.length > 0) {
      return (
        <div className="flex shrink-0 flex-wrap items-center gap-1">
          {schoolBoundaryPreviews.map(({ id, ring }) => (
            <PolygonPreview
              key={id}
              coordinates={ring}
              size={PREVIEW_SIZE}
              {...polygonTint}
            />
          ))}
        </div>
      );
    }

    if (previewCoordinatesProp) {
      return (
        <PolygonPreview
          coordinates={previewCoordinatesProp}
          size={PREVIEW_SIZE}
          {...polygonTint}
        />
      );
    }
    return null;
  }, [
    isLoading,
    polygonTint,
    previewFromCustom,
    schoolBoundaryPreviews,
    previewCoordinatesProp,
  ]);

  return (
    <Card className="flex flex-col gap-2 p-2.5 pl-3">
      <div className="flex items-center gap-2">
        {previewContent}
        <div className="flex min-w-0 flex-1 flex-col">
          <Typography.TableTitleXS colorShade={300}>
            {label}
          </Typography.TableTitleXS>
          <Typography.BodyMMedium
            capitalize={false}
            className="min-w-0 text-main-600"
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="block truncate">{displayValue}</span>
              </TooltipTrigger>
              <TooltipContent
                className="max-w-[360px] whitespace-normal"
                showArrow={false}
              >
                {displayValue}
              </TooltipContent>
            </Tooltip>
          </Typography.BodyMMedium>
        </div>

        {onRemove && (
          <IconButton
            icon={CancelIcon}
            variant="secondary"
            onClick={onRemove}
          />
        )}
      </div>

      {noteText !== "" ? (
        <div>
          <Typography.TableTitleXS colorShade={300}>
            {dictionaries.mapPage.boundaryPanel.note}
          </Typography.TableTitleXS>
          <Typography.BodyMMedium
            capitalize={false}
            className="mt-1 whitespace-pre-wrap break-words"
            colorShade={500}
          >
            <LinkifiedText text={noteText} />
          </Typography.BodyMMedium>
        </div>
      ) : null}
    </Card>
  );
};

export default React.memo(ShapeCardComponent);
